import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api/client';

// ── Colour palette ────────────────────────────────────────────────────────────
const AS_COLOURS: Record<string, string> = {
  Healthy:     '#16a34a',
  Monitor:     '#0891b2',
  Investigate: '#d97706',
  Action:      '#ea580c',
  Urgent:      '#dc2626',
  'No data':   '#94a3b8',
};

const AXIS_COLOURS = ['#3b82f6', '#10b981', '#06b6d4'];

function labelColour(label: string | null | undefined): string {
  return AS_COLOURS[label ?? 'No data'] ?? '#94a3b8';
}

// ── Date formatting ───────────────────────────────────────────────────────────
function fmtDate(ts: string): string {
  if (!ts) return '';
  const sep = ts.includes('/') ? '/' : '-';
  const parts = ts.split(' ')[0].split(sep).map(Number);
  const [, m, d] = parts;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[m - 1]} ${d}`;
}

function fmtFull(ts: string): string {
  if (!ts) return '';
  const sep = ts.includes('/') ? '/' : '-';
  const [datePart, timePart = '00:00:00'] = ts.split(' ');
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12  = hh % 12 || 12;
  return `${months[m-1]} ${d}, ${y}  ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

// ── Reference band definitions ────────────────────────────────────────────────
const BANDS = [
  { y1: 90, y2: 100, fill: '#16a34a', label: 'Healthy',     labelFill: '#16a34a' },
  { y1: 70, y2: 90,  fill: '#0891b2', label: 'Monitor',     labelFill: '#0891b2' },
  { y1: 50, y2: 70,  fill: '#d97706', label: 'Investigate', labelFill: '#d97706' },
  { y1: 25, y2: 50,  fill: '#ea580c', label: 'Action',      labelFill: '#ea580c' },
  { y1: 0,  y2: 25,  fill: '#dc2626', label: 'Urgent',      labelFill: '#dc2626' },
];
const THRESHOLD_LINES = [
  { y: 70, stroke: '#0891b2' },
  { y: 50, stroke: '#d97706' },
  { y: 25, stroke: '#ea580c' },
];
const ALL_TICKS = [0, 25, 50, 70, 90, 100];

// ── Props ─────────────────────────────────────────────────────────────────────
export interface AnomalyScoreChartProps {
  machineId:    string;
  pointIndex?:  number;
  days?:        number;
  onPointClick?: (fileId: number, axisId: number, measuredAt: string, pointName: string) => void;
  /** When true, skip the outer card wrapper + header — renders inline inside a parent card */
  embedded?:    boolean;
  /** When true, hide the internal day-picker (parent controls days via the days prop) */
  hideControls?: boolean;
}

// ── Custom dot ────────────────────────────────────────────────────────────────
function ASDot(props: any) {
  const { cx, cy, payload, onPointClick } = props;
  if (!cx || !cy) return null;
  const colour = labelColour(payload.anomaly_label);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={colour}
      stroke="#fff"
      strokeWidth={1.5}
      style={{ cursor: onPointClick ? 'pointer' : 'default' }}
      onClick={() => {
        if (onPointClick && payload.file_id && payload.axis_id) {
          onPointClick(payload.file_id, payload.axis_id, payload.measured_at, payload.point_name ?? '');
        }
      }}
    />
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
function ASTooltip({ active, payload, level, onPointClick }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const colour = labelColour(d.anomaly_label);
  const hasWf = d.file_id && d.axis_id;
  const displayScore = d.anomaly_score ?? d.value;
  const rawScore = d.anomaly_score_raw;
  const isSmoothed = rawScore != null && rawScore !== displayScore;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm max-w-[220px]">
      {level === 'point' ? (
        <p className="font-medium text-gray-700 mb-1">Axis: {payload[0]?.name ?? ''}</p>
      ) : (
        <p className="font-medium text-gray-700 mb-1">{d.point_name ?? payload[0]?.name ?? ''}</p>
      )}
      <p className="text-gray-500 text-xs mb-2">{fmtFull(d.measured_at)}</p>
      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold" style={{ color: colour }}>{displayScore}</span>
        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: colour }}>
          {d.anomaly_label ?? ''}
        </span>
      </div>
      {isSmoothed && (
        <p className="text-xs text-slate-400 mt-1">Raw reading: {rawScore}</p>
      )}
      {hasWf && onPointClick && (
        <p className="text-xs text-[#0891b2] mt-2 cursor-pointer hover:underline"
          onClick={() => onPointClick(d.file_id, d.axis_id, d.measured_at, d.point_name ?? '')}>
          Click dot to view waveform →
        </p>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function computeYDomain(visibleValues: number[]): [number, number] {
  if (!visibleValues.length) return [0, 100];
  const rawMin = Math.min(...visibleValues);
  const rawMax = Math.max(...visibleValues);
  const range = rawMax - rawMin;
  const pad = Math.max(3, range * 0.08);
  return [
    Math.max(0,   Math.floor(rawMin - pad)),
    Math.min(100, Math.ceil(rawMax  + pad)),
  ];
}

// ── Main component ────────────────────────────────────────────────────────────
export function AnomalyScoreChart({ machineId, pointIndex, days = 90, onPointClick, embedded = false, hideControls = false }: AnomalyScoreChartProps) {
  const [data, setData]               = useState<any | null>(null);
  const [loading, setLoading]         = useState(true);
  const [selectedDays, setSelectedDays] = useState(days);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const dayOptions = [90, 180, 365];

  // Sync external days prop when parent controls the time range
  useEffect(() => { setSelectedDays(days); }, [days]);

  useEffect(() => {
    setLoading(true);
    const url = pointIndex != null
      ? `/machines/${machineId}/anomaly-trend?days=${selectedDays}&point_index=${pointIndex}`
      : `/machines/${machineId}/anomaly-trend?days=${selectedDays}`;
    apiFetch<any>(url)
      .then(d => { setData(d); setHiddenSeries(new Set()); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [machineId, pointIndex, selectedDays]);

  const toggleSeries = (name: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  // ── Build chart series ──────────────────────────────────────────────────────
  const { series, isPointLevel } = useMemo(() => {
    if (!data) return { series: [], isPointLevel: false };

    if (data.level === 'machine') {
      return {
        isPointLevel: false,
        series: (data.points ?? []).map((pt: any, i: number) => ({
          name:     pt.point_name,
          colour:   AXIS_COLOURS[i % AXIS_COLOURS.length],
          readings: (pt.readings ?? []).map((r: any) => ({
            ...r,
            time:  r.measured_at,
            value: r.anomaly_score,
            point_name: pt.point_name,
          })),
        })),
      };
    } else {
      const axisColourMap: Record<number, string> = { 1: AXIS_COLOURS[0], 2: AXIS_COLOURS[1], 3: AXIS_COLOURS[2] };
      return {
        isPointLevel: true,
        series: (data.axes ?? []).map((ax: any) => ({
          name:     ax.axis_name,
          colour:   axisColourMap[ax.axis_num] ?? '#94a3b8',
          axis_num: ax.axis_num,
          readings: (ax.readings ?? []).map((r: any) => ({
            ...r,
            time:  r.measured_at,
            value: r.anomaly_score,
          })),
        })),
      };
    }
  }, [data]);

  // Merged time axis for machine level
  const mergedData = useMemo(() => {
    if (isPointLevel || !series.length) return [];
    const map = new Map<string, any>();
    for (const s of series) {
      for (const r of s.readings) {
        if (!map.has(r.time)) map.set(r.time, { time: r.time });
        map.get(r.time)![s.name] = r.value;
        map.get(r.time)![`_payload_${s.name}`] = r;
      }
    }
    return Array.from(map.values()).sort((a, b) => a.time.localeCompare(b.time));
  }, [isPointLevel, series]);

  // ── Autoscale domain from visible series ──────────────────────────────────
  const [yMin, yMax] = useMemo(() => {
    const vals: number[] = [];
    if (isPointLevel) {
      for (const s of series) {
        if (hiddenSeries.has(s.name)) continue;
        for (const r of s.readings) {
          if (r.value != null) vals.push(r.value);
        }
      }
    } else {
      for (const s of series) {
        if (hiddenSeries.has(s.name)) continue;
        for (const row of mergedData) {
          const v = row[s.name];
          if (v != null) vals.push(v);
        }
      }
    }
    return computeYDomain(vals);
  }, [series, mergedData, isPointLevel, hiddenSeries]);

  const yTicks = ALL_TICKS.filter(t => t >= yMin && t <= yMax);

  // ── Render ──────────────────────────────────────────────────────────────────
  const hasData = isPointLevel
    ? series.some(s => s.readings.length > 0)
    : mergedData.length > 0;

  const sharedAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
      {BANDS.map(b => {
        const by1 = Math.max(b.y1, yMin);
        const by2 = Math.min(b.y2, yMax);
        if (by2 <= by1) return null;
        // Only show label when center of band is within range
        const center = (b.y1 + b.y2) / 2;
        const showLabel = center >= yMin && center <= yMax;
        return (
          <ReferenceArea
            key={b.label}
            y1={by1}
            y2={by2}
            fill={b.fill}
            fillOpacity={0.07}
            label={showLabel ? { value: b.label, position: 'right', fontSize: 9, fill: b.labelFill } : undefined}
          />
        );
      })}
      {THRESHOLD_LINES.map(tl => (
        tl.y > yMin && tl.y < yMax
          ? <ReferenceLine key={tl.y} y={tl.y} stroke={tl.stroke} strokeDasharray="4 2" strokeWidth={1} />
          : null
      ))}
      <YAxis
        stroke="#9CA3AF"
        domain={[yMin, yMax]}
        ticks={yTicks}
        label={{ value: 'Anomaly Score', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#9CA3AF', offset: 10 }}
      />
    </>
  );

  const chartContent = (
    <>
      {/* Header — hidden when embedded (parent card provides context) */}
      {!embedded && (
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-gray-900">Anomaly Score</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {isPointLevel
              ? 'All axes — click any point to view waveform'
              : 'Worst axis per measurement point — click any point to view waveform'}
          </p>
        </div>
        <div className="flex gap-1">
          {dayOptions.map(d => (
            <button
              key={d}
              onClick={() => setSelectedDays(d)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                selectedDays === d
                  ? 'bg-[#0891b2] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {d === 90 ? '90d' : d === 180 ? '180d' : '365d'}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Subtitle when embedded */}
      {embedded && (
        <p className="text-xs text-gray-500 mb-3">
          {isPointLevel
            ? 'All axes — click any point to view waveform'
            : 'Worst axis per measurement point — click any point to view waveform'}
        </p>
      )}

      {/* Chart body */}
      {loading ? (
        <div className="h-[320px] bg-gray-100 rounded-lg animate-pulse" />
      ) : !hasData ? (
        <div className="flex items-center justify-center h-[320px] text-gray-400 text-sm text-center px-4">
          No anomaly score data available for this machine.<br />
          <span className="text-xs mt-1 block">Waveform data (scheduled readings) is required.</span>
        </div>
      ) : isPointLevel ? (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart margin={{ top: 8, right: 64, left: 0, bottom: 0 }}>
            {sharedAxes}
            <XAxis dataKey="time" type="category" allowDuplicatedCategory={false} stroke="#9CA3AF" tickFormatter={fmtDate} tick={{ fontSize: 10 }} />
            <Tooltip content={<ASTooltip level="point" onPointClick={onPointClick} />} />
            {series.map((s) => (
              <Line
                key={s.name}
                data={s.readings}
                dataKey="value"
                name={s.name}
                type="monotone"
                stroke={s.colour}
                strokeWidth={2}
                connectNulls
                hide={hiddenSeries.has(s.name)}
                isAnimationActive={false}
                dot={(p: any) => (
                  <ASDot key={p.index} {...p} payload={p.payload} onPointClick={onPointClick} />
                )}
                activeDot={{ r: 7, fill: s.colour, stroke: '#fff', strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={mergedData} margin={{ top: 8, right: 64, left: 0, bottom: 0 }}>
            {sharedAxes}
            <XAxis dataKey="time" stroke="#9CA3AF" tickFormatter={fmtDate} tick={{ fontSize: 10 }} />
            <Tooltip content={<ASTooltip level="machine" onPointClick={onPointClick} />} />
            {series.map((s) => (
              <Line
                key={s.name}
                dataKey={s.name}
                type="monotone"
                stroke={s.colour}
                strokeWidth={2}
                connectNulls
                hide={hiddenSeries.has(s.name)}
                isAnimationActive={false}
                dot={(p: any) => {
                  const payload = p.payload?.[`_payload_${s.name}`];
                  if (!payload) return <circle key={p.index} r={0} />;
                  return (
                    <ASDot key={p.index} cx={p.cx} cy={p.cy} payload={payload} onPointClick={onPointClick} />
                  );
                }}
                activeDot={{ r: 7, fill: s.colour, stroke: '#fff', strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Clickable legend */}
      {!loading && hasData && (
        <div className="flex flex-wrap gap-3 mt-4 justify-center">
          {series.map((s, i) => {
            const hidden = hiddenSeries.has(s.name);
            return (
              <button
                key={i}
                onClick={() => toggleSeries(s.name)}
                className={`flex items-center gap-1.5 text-xs transition-opacity select-none ${
                  hidden ? 'opacity-30' : 'text-gray-600'
                }`}
                title={hidden ? `Show ${s.name}` : `Hide ${s.name}`}
              >
                <span
                  className="inline-block w-4 h-0.5 rounded"
                  style={{ backgroundColor: s.colour }}
                />
                {s.name}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  if (embedded) return chartContent;

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
      {chartContent}
    </div>
  );
}
